// eslint-disable-next-line import/no-unresolved
import { CustomerSucessController } from "@talentbait/shared-components";

import logger from "../../../../utils/LoggerFrontend";
import moment from "moment";
import _ from "lodash";
import firstBy from "thenby";
const { scaleLinear } = require("d3-scale");

import { takeScreenShot, download } from "./utils";

// Helper method to send message to customer success
export const customerSuccessSender = async (message, data) => {
  CustomerSucessController.send(message, data);
  logger.log(
    "AdminRead--Creatives_Performance:customerSuccessSender",
    "Message sent to customer success",
    { message, data }
  );
};

export const createGetAggregatedCreativesMetrics = (fbChannelCampaigns) => () => {
  const metrics = {};

  // Gets the latest channel campaign Date, which is the active one normally
  const latestChannelCampaignDate = fbChannelCampaigns
    .map((cc) => {
      return cc.createdDate;
    })
    .sort()
    .reverse()[0];

  // Get which channel
  const channelCampaignToUse = fbChannelCampaigns.find(
    (cc) => cc.createdDate == latestChannelCampaignDate
  );

  if (!channelCampaignToUse) {
    return [];
  }

  const { channel, platformCampaignId, adsetCampaignId } = channelCampaignToUse;

  for (const creativeMetrics of channelCampaignToUse.creativesMetrics) {
    let results = {};
    if (creativeMetrics.campaignGoal !== "traffic") {
      results = {
        field: "conversions",
        fieldText: "Conversions",
        value: parseInt(creativeMetrics.conversions || 0)
      };
    } else {
      results = {
        field: "reach",
        fieldText: "Reach",
        value: parseInt(creativeMetrics.reach || 0)
      };
    }

    // Transform null values to 0, so they can be sorted correctly later
    if (creativeMetrics.ctr == null) {
      creativeMetrics.ctr = 0;
    }

    metrics[creativeMetrics.asset.id] = {
      channel,
      platformCampaignId,
      adsetCampaignId,
      results,
      ...creativeMetrics
    };
  }

  const aggregatedValues = Object.keys(metrics).map((assetId) => metrics[assetId]);
  return aggregatedValues;
};

export const createDownloadScreenshot = (setShowAdminData, userProfile, refForScreenShot) => () => {
  setShowAdminData(false);
  takeScreenShot(refForScreenShot.current)
    .then(download)
    .finally(async () => {
      setShowAdminData(true);
      // Send to customer success a new message notification
      customerSuccessSender("CampaignAdmin:CreativeMetricsPerformance:Download");
      logger.log(
        "CampaignAdmin:CreativeMetricsPerformance:DownloadScreenshot",
        "Table screenshot download",
        { user: userProfile }
      );
    })
    .catch((error) => {
      logger.log(
        "CampaignAdmin:CreativeMetricsPerformance:DownloadScreenshot",
        "Error downloading screenshot",
        { error }
      );
    });
};

export const createGetDates = (fbChannelCampaigns) => () => {
  for (const channelCampaign of fbChannelCampaigns) {
    const lifetimeMetrics = channelCampaign?.metrics || {};

    const { startDate, endDate } = lifetimeMetrics;

    if (!startDate || !moment(startDate).isValid() || !endDate || !moment(endDate).isValid())
      continue;

    const activeDays = moment().diff(moment(startDate), "d");

    return {
      startDate,
      endDate,
      activeDays
    };
  }

  return {
    startDate: undefined,
    endDate: undefined,
    activeDays: undefined
  };
};

export const createGetHighestImpressionsAndSortedMetrics =
  ({ creativesMetrics, defaultSortingField, defaultSortOrder }) =>
  () => {
    // Iterates creative metrics and renders Metrics table
    const creativesMetricsSorted = creativesMetrics
      .map((creatives) => {
        creatives.conversions = parseInt(creatives.conversions || 0, 10);
        return creatives;
      })
      .sort(
        firstBy((element) => {
          return element.impressions < 1000;
        }).thenBy((a, b) => {
          if (defaultSortOrder == 1) {
            return (
              parseFloat(_.get(a, defaultSortingField)) - parseFloat(_.get(b, defaultSortingField))
            );
          }
          if (defaultSortOrder == -1) {
            return (
              parseFloat(_.get(b, defaultSortingField)) - parseFloat(_.get(a, defaultSortingField))
            );
          }
          return 0;
        })
      );

    // Gets all impressions values
    const impressionsValues = creativesMetricsSorted
      .map((metric) => metric.impressions)
      .filter((impression) => impression != undefined);

    // Find impression with highest value
    const highestImpressionValue = Math.max(...impressionsValues);

    logger.log("AdminRead--Creatives__Performance", "highest impression index: ", {
      highestImpressionValue
    });

    return { highestImpressionValue, creativesMetricsSorted };
  };

export const calculateCTRBarsScale = (creativesMetrics) => {
  const bestCTR = creativesMetrics
    .map((metrics) => parseFloat(metrics.ctr))
    .sort()
    .reverse();

  const maxCTR = Math.max(...bestCTR);
  const scale = scaleLinear().domain([0, maxCTR]).range([0, 150]);

  return scale;
};

export const calculateCTRValue = ({ inlineClicks, impressions }) => {
  const ctr = parseFloat((inlineClicks / impressions) * 100);
  return ctr;
};

// Main function to create creativesMetrics
export const createComponentData = (channelCampaigns, defaultSortingField, defaultSortOrder) => {
  // Gets Facebook and Instagram channel campaigns
  const fbChannelCampaigns = (channelCampaigns || [])
    .filter((channelCampaign) => {
      return channelCampaign.channel == "facebook" || channelCampaign.channel == "instagram";
    })
    .filter((channelCampaign) => {
      return channelCampaign.creativesMetrics;
    });

  // Get start end and active days
  const { startDate, endDate, activeDays } = createGetDates(fbChannelCampaigns);

  // Maps creative metrics
  const creativesMetrics = createGetAggregatedCreativesMetrics(fbChannelCampaigns)();
  const metricsLastFetchDay = moment(creativesMetrics[0]?.snapshot_date).format("DD.MM.YYYY");

  // Defines highestImpressionsValue and creative metrics sorted
  const { highestImpressionValue, creativesMetricsSorted } =
    createGetHighestImpressionsAndSortedMetrics({
      creativesMetrics,
      defaultSortingField,
      defaultSortOrder
    })();

  const scale = calculateCTRBarsScale(creativesMetrics);
  return {
    startDate,
    endDate,
    activeDays,
    highestImpressionValue,
    scale,
    creativesMetricsSorted,
    metricsLastFetchDay
  };
};
